import type {
  ProductRateDetailType,
  ProductTariffRequest,
  ProductTariffsResponseDetail,
} from '@monorepo-types/dc/sales';

/*
 * Shared by both `ModelContractTable` lanes — the legacy Sitecore component
 * (`libs/sitecore/components/content/src/ModelContractTable/`) and the CMS-agnostic one
 * (`libs/cms-components/src/ModelContractTable.tsx`). It lives here rather than beside either of
 * them because `lib:cms-components` may not depend on `lib:sitecore-components`, and the
 * alternative — a second copy of the marketing product codes — would let the two lanes quote
 * different prices for a legally-mandated model contract if a code ever changed.
 */

/**
 * Marketing Product Codes of the model contract products, per label. Labels that are absent do not publish a model
 * contract, so no tariffs are requested for them.
 */
const MARKETING_PRODUCT_CODES = {
  eneco: { electricityVariable: 'POMVW', electricityFixed: 'POMP1', gasVariable: 'PAMVW', gasFixed: 'PAMP1' },
  oxxio: { electricityVariable: 'XEMVW', electricityFixed: 'XEMP1', gasVariable: 'XGMVW', gasFixed: 'XGMP1' },
} as const;

type ModelContractCodes = (typeof MARKETING_PRODUCT_CODES)[keyof typeof MARKETING_PRODUCT_CODES];

/** The placeholders authors put in the Sitecore table cells, mapped to the tariff they resolve to. */
export type ValuesDictionary = Record<string, number | undefined>;

export function getMarketingProductCodes(label?: string): ModelContractCodes | undefined {
  return MARKETING_PRODUCT_CODES[label as keyof typeof MARKETING_PRODUCT_CODES];
}

export function getProductTariffsProducts(codes: ModelContractCodes): ProductTariffRequest[] {
  return [
    { marketingProductCode: codes.electricityVariable, meterType: 'double' },
    { marketingProductCode: codes.electricityVariable, meterType: 'single' },
    { marketingProductCode: codes.electricityFixed, meterType: 'double' },
    { marketingProductCode: codes.electricityFixed, meterType: 'single' },
    { marketingProductCode: codes.gasVariable, meterType: 'single' },
    { marketingProductCode: codes.gasFixed, meterType: 'single' },
  ];
}

function getRate(
  product: ProductTariffsResponseDetail | undefined,
  type: ProductRateDetailType,
  field: 'vatIncluded' | 'vatExcluded' = 'vatIncluded',
): number | undefined {
  return product?.productRateDetails?.find(rateDetail => rateDetail.type === type)?.[field];
}

export function getTariffValues(
  codes: ModelContractCodes | undefined,
  productTariffs: ProductTariffsResponseDetail[] | null | undefined,
): ValuesDictionary {
  if (!codes || !productTariffs) return {};

  const findProduct = (marketingProductCode: string, meterType?: string) =>
    productTariffs.find(
      tariff => tariff.marketingProductCode === marketingProductCode && (!meterType || tariff.meterType === meterType),
    );

  // Electricity products-----------------------------------

  // Single
  const singleVariableElectricityProduct = findProduct(codes.electricityVariable, 'single');
  const singleFixedElectricityProduct = findProduct(codes.electricityFixed, 'single');
  // Double
  const doubleVariableElectricityProduct = findProduct(codes.electricityVariable, 'double');
  const doubleFixedElectricityProduct = findProduct(codes.electricityFixed, 'double');

  // Gas ----------------------------------------------

  const gasVariableProduct = findProduct(codes.gasVariable);
  const gasFixedProduct = findProduct(codes.gasFixed);

  return {
    '{stroomEnkelVariabele}': getRate(singleVariableElectricityProduct, 'tariff'),
    '{stroomEnkelVast}': getRate(singleFixedElectricityProduct, 'tariff'),
    '{stroomNormaalVariabele}': getRate(doubleVariableElectricityProduct, 'tariff'),
    '{stroomNormaalVast}': getRate(doubleFixedElectricityProduct, 'tariff'),
    '{stroomDaalVariabele}': getRate(doubleVariableElectricityProduct, 'lowTariff'),
    '{stroomDaalVast}': getRate(doubleFixedElectricityProduct, 'lowTariff'),
    '{vasteLeveringskostenStroomPerMaandVariabele}': getRate(doubleVariableElectricityProduct, 'standingCharge'),
    '{vasteLeveringskostenStroomPerMaandVast}': getRate(doubleFixedElectricityProduct, 'standingCharge'),
    '{terugleverkostenTot1Jan27Variabele}': getRate(doubleVariableElectricityProduct, 'redeliveryCostTariff'),
    '{terugleverkostenTot1Jan27Vast}': getRate(doubleFixedElectricityProduct, 'redeliveryCostTariff'),
    '{terugleverkostenVanaf1Jan2027Variabele}':
      doubleVariableElectricityProduct?.productFutureRate?.zonOpDak?.vatIncluded,
    '{terugleverkostenVanaf1Jan2027Vast}': doubleFixedElectricityProduct?.productFutureRate?.zonOpDak?.vatIncluded,
    '{terugleververgoedingTot1Jan2027Variabele}': getRate(
      doubleVariableElectricityProduct,
      'redeliveryTariff',
      'vatExcluded',
    ),
    '{terugleververgoedingTot1Jan2027Vast}': getRate(doubleFixedElectricityProduct, 'redeliveryTariff', 'vatExcluded'),
    '{terugleververgoedingVanaf1Jan2027Variabele}':
      doubleVariableElectricityProduct?.productFutureRate?.electricityRedeliveryRefund?.vatExcluded,
    '{terugleververgoedingVanaf1Jan2027Vast}':
      doubleFixedElectricityProduct?.productFutureRate?.electricityRedeliveryRefund?.vatExcluded,
    '{gasM3Variabele}': getRate(gasVariableProduct, 'tariff'),
    '{gasM3Vast}': getRate(gasFixedProduct, 'tariff'),
    '{vasteLeveringskostenGasPerMaandVariabele}': getRate(gasVariableProduct, 'standingCharge'),
    '{vasteLeveringskostenGasPerMaandVast}': getRate(gasFixedProduct, 'standingCharge'),
  };
}
